import phone from 'phone'
import * as Yup from 'yup'
import TextSingleLine from 'Survey/components/question-types/TextSingleLine'
import LongText from 'Survey/components/question-types/LongText'
import MultipleChoice from 'Survey/components/question-types/MultipleChoice'
import MultipleSelect from 'Survey/components/question-types/MultipleSelect'
import Date from 'Survey/components/question-types/Date'
import File from 'Survey/components/question-types/File'
import Checkbox from 'Survey/components/question-types/Checkbox'
import Score from 'Survey/components/question-types/Score'
import PostCode from 'Shared/components/Postcode/Postcode'
import PhoneNumber from 'Survey/components/question-types/PhoneNumber'
import OptInRadio from 'Survey/components/question-types/OptInRadio'
import Names from 'Survey/components/question-types/Names'
import { getPostcodeCountryData } from 'Shared/lib/postcodes'
import useFlowTranslate from 'FlowBuilder/hooks/useFlowTranslate'
import ResponsesConfirmation from 'Survey/components/question-types/ResponsesConfirmation'
import isValidEmail from 'Shared/functions/validateEmail'

const questionTypeComponents = {
  'text': TextSingleLine,
  'long_text': LongText,
  'multiple_choice': MultipleChoice, // radios
  'multiple_select': MultipleSelect, // checkboxes
  'checkbox': Checkbox,
  'score': Score,
  'ranking': Score,
  'name': Names,
  'email': TextSingleLine,
  'postcode': PostCode,
  'phone_number': PhoneNumber,
  'instruction': () => <div />,
  'opt_in_radio': OptInRadio,
  'date': Date,
  'file': File,
  'responses_confirmation': ResponsesConfirmation,
  'unknown': () => <div>Unknown</div>,
}

const personalQuestionTypes = ['name', 'email', 'postcode', 'phone_number', 'address_1', 'city']

function getValidationSchema(questions) {
  const { fallback } = useFlowTranslate();

  return Yup.object().shape(
    questions.reduce((acc, question) => {
      const { id, questionType, settings, hide } = question
      if (hide || questionType === 'detailsMessage') return acc

      let validationRule = Yup.string()

      if (['multiple_select', 'file'].includes(questionType)) {
        validationRule = Yup.array().of(Yup.string())
      }

      // Split names are stored as { first_name, last_name } object
      if (questionType === 'name' && settings?.splitNames) {
        validationRule = Yup.mixed().test(
          'both-names-required',
          fallback.validationEnterBothNames,
          (value) => {
            if (!settings?.required) return true
            if (!value || typeof value !== 'object') return false
            return value.first_name?.trim() && value.last_name?.trim()
          }
        )
        acc[id] = validationRule
        return acc
      }

      if (settings?.required) {
        switch (questionType) {
          case 'opt_in_radio':
          case 'multiple_choice':
          case 'multiple_select':
          case 'score':
            validationRule = validationRule.required(fallback.validationSelectAnOption)
            break
          case 'checkbox':
            validationRule = validationRule.required(fallback.validationConfirmCheckbox)
            validationRule = validationRule.oneOf(['y'], fallback.validationConfirmCheckbox)
            break
          case 'date':
            validationRule = validationRule.required(fallback.validationChooseADate)
            break
          case 'file':
            validationRule = validationRule.required(fallback.validationUploadAFile)
            break
          default:
            validationRule = validationRule.required(fallback.validationEnterAResponse)
            break
        }

        if (questionType === 'multiple_select') {
          validationRule = validationRule.min(1, fallback.validationSelectAtleastOneOption)
        }
      }

      // Add max validation for multiple_select (independent of required)
      if (questionType === 'multiple_select' && settings?.enableMaxAnswers && settings?.maxAnswers) {
        const message = fallback.validationSelectMaximumOptions.replace(
          '{{ max_answers }}',
          settings.maxAnswers.toString()
        )
        validationRule = validationRule.max(settings.maxAnswers, message)
      }

      switch (questionType) {
        case 'email':
          validationRule = validationRule.test(
            'is-valid-email',
            fallback.validationEmail,
            (value) => {
              if (!value) return !settings?.required
              return isValidEmail(value)
            }
          )
          break

        case 'postcode':
          validationRule = Yup.mixed().test(
            'is-valid-postcode',
            (value) => {
              try
              {
                const countryData = getPostcodeCountryData(value?.value?.country)
                if (!countryData) return fallback.validationPostcode
                return fallback.validationPostcodeExpected.replace('{{ example_postcode }}', countryData?.Zip_Format)
              } catch (e) {
                return fallback.validationPostcode
              }
            },
            (value) => {
              if (!value) return !(settings?.required)
              if (typeof value === 'string') {
                value = {
                  country: window.ENV.COUNTRY,
                  postcode: value
                }
              }
              if (!value || typeof value !== 'object' || !value.postcode || !value.country) return !(settings?.required)
              const cleanPostcode = value.postcode.replace(/\s/g, '').toUpperCase()
              const countryData = getPostcodeCountryData(value.country)
              if (!countryData) return false
              return RegExp(countryData.Regex).test(cleanPostcode)
            }
          )
          break

        case 'phone_number':
          validationRule = validationRule.test(
            'is-valid-phone',
            fallback.validationPhone,
            (value) => {
              if (!value) return true
              return phone(value).isValid
            }
          )
          break

        case 'responses_confirmation':
          validationRule = validationRule.required(fallback.validationConfirmAnswers)
          validationRule = validationRule.oneOf(['y'], fallback.validationConfirmAnswers)
          break

        default:
          break
      }

      acc[id] = validationRule
      return acc
    }, {})
  )
}

function transformQuestionsWithTargetDetails(questions, targetDetails, { detailsFirst = false } = {}) {
  if (!targetDetails || !targetDetails.signedIn) {
    return questions
  }
  const detailsPresent = targetDetails.detailsPresent || {}

  let anyHidden = false
  const transformedQuestions = questions.map((q) => {
     if (personalQuestionTypes.includes(q.questionType) && detailsPresent[_.camelCase(q.questionType)]) {
      anyHidden = true
      return {
        ...q,
        hide: true
      }
    } else {
      return q
    }
  })

  // if any have been hidden, insert a message at the index of the last hidden question in the
  if (anyHidden) {
    if (detailsFirst) {
      const firstHiddenIndex = transformedQuestions.findIndex(q => q.hide)
      transformedQuestions.splice(firstHiddenIndex, 0, { questionType: 'detailsMessage', signedInAs: targetDetails.signedInAs })
    } else {
      const lastHiddenIndex = transformedQuestions.map(q => q.hide).lastIndexOf(true)
      transformedQuestions.splice(lastHiddenIndex + 1, 0, { questionType: 'detailsMessage', signedInAs: targetDetails.signedInAs })
    }
  }

  return transformedQuestions
}

export {
  questionTypeComponents,
  getValidationSchema,
  transformQuestionsWithTargetDetails
}
